// apps/frontend/src/pages/pipelines/hooks/use-preview-data.ts
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { PipelineNode, PipelineEdge } from '../types/pipeline';
import { useEffect, useState } from 'react';
import { PipelinePreviewExecutor } from '@/lib/pipeline-preview/executor';

// Hash function for config
function hashConfig(config: unknown): string {
  return JSON.stringify(config);
}

// Check if node config is valid for preview
function isConfigValid(node: PipelineNode | null): boolean {
  if (!node) return false;

  // SOURCE node needs at least a data source selected
  if (node.type === 'SOURCE') {
    const config = node.data.config as any;
    return !!config?.dataSourceId;
  }

  return true;
}

interface UsePreviewDataOptions {
  pipelineId: string;
  node: PipelineNode | null;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  sampleSize?: number;
  enabled?: boolean;
}

export function usePreviewData({
  pipelineId,
  node,
  nodes,
  edges,
  sampleSize = 50,
  enabled = true,
}: UsePreviewDataOptions) {
  const [debouncedNode, setDebouncedNode] = useState(node);

  // Debounce node config changes (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNode(node);
    }, 500);

    return () => clearTimeout(timer);
  }, [node]);

  const configHash = hashConfig(debouncedNode?.data);

  return useQuery({
    queryKey: ['preview', pipelineId, debouncedNode?.id, configHash, sampleSize],
    queryFn: async () => {
      if (!debouncedNode) {
        throw new Error('No node selected');
      }

      console.log('[DEBUG] Preview request for node:', {
        nodeId: debouncedNode.id,
        nodeType: debouncedNode.type,
        nodeDataType: debouncedNode.data.type,
        nodeConfig: debouncedNode.data.config,
      });

      // WORKAROUND: For SOURCE nodes, use the working data source preview endpoint directly
      if (debouncedNode.data.type === 'SOURCE') {
        const config = debouncedNode.data.config as any;
        const dataSourceId = config?.dataSourceId;

        if (!dataSourceId) {
          throw new Error('No data source configured');
        }

        console.log('[DEBUG] Using direct data source preview for SOURCE node, dataSourceId:', dataSourceId);

        // Call the working data source preview endpoint
        const dataSourcePreview = await apiClient.get<{
          data: unknown[];
          totalRows: number;
          jobId: string;
        }>(`/preview/data-sources/${dataSourceId}`);

        console.log('[DEBUG] Data source preview response:', dataSourcePreview);

        // Transform to PreviewData format
        const preview = {
          nodeId: debouncedNode.id,
          data: dataSourcePreview.data.slice(0, sampleSize),
          schema: {
            columns: dataSourcePreview.data.length > 0
              ? Object.keys(dataSourcePreview.data[0] as Record<string, unknown>).map(key => ({
                  name: key,
                  type: inferType((dataSourcePreview.data[0] as any)[key]),
                  nullable: true,
                }))
              : [],
          },
          stats: {
            totalRows: dataSourcePreview.totalRows,
            sampledRows: Math.min(dataSourcePreview.data.length, sampleSize),
            executionTime: 0,
          },
          metadata: {
            sourceNode: dataSourceId,
            nodeType: 'SOURCE',
          },
        };

        return preview;
      }

      // For other node types, use client-side executor
      console.log('[DEBUG] Using client-side executor for node type:', debouncedNode.data.type);

      const executor = new PipelinePreviewExecutor(
        nodes as PipelineNode[],
        edges as PipelineEdge[]
      );

      const result = await executor.execute(debouncedNode.id);

      if (!result.success) {
        throw new Error(result.error || 'Execution failed');
      }

      console.log('[DEBUG] Executor result:', result);

      // Transform to PreviewData format
      const preview = {
        nodeId: debouncedNode.id,
        data: result.data,
        schema: result.schema,
        stats: {
          totalRows: null,
          sampledRows: result.stats.rows,
          executionTime: result.stats.executionTime,
        },
        metadata: {
          sourceNode: debouncedNode.id,
          nodeType: debouncedNode.data.type,
        },
      };

      return preview;
    },
    enabled: enabled && !!debouncedNode && isConfigValid(debouncedNode),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
}

// Helper to infer column type from value
function inferType(value: unknown): 'string' | 'number' | 'boolean' | 'date' | 'json' | 'unknown' {
  if (value === null || value === undefined) return 'unknown';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string') {
    // Check if it looks like a date
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
    return 'string';
  }
  if (typeof value === 'object') return 'json';
  return 'unknown';
}
